import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { agentApi, type AgentThread } from '@/lib/api/agent'
import { apiClient } from '@/lib/api/client'
import type { Consignment, Inquiry } from '@/lib/api/types'
import {
  assistantSessionReducer,
  initialAssistantSession,
  type AssistantSessionState,
  type PageContext,
} from '@/src/lib/assistant/session'
import type { MentionItem } from './MentionCombobox'
import { useAssistant, type AssistantController } from './useAssistant'

interface AssistantSessionValue extends AssistantSessionState {
  assistant: AssistantController
  threads: AgentThread[]
  mentionItems: MentionItem[]
  /** Something is showing the assistant: load its lists, once. */
  activate: () => void
  /** The signed-in person; a different one gets a clean session. */
  bindUser: (userId: string) => void
  openPanel: () => void
  closePanel: () => void
  selectThread: (threadId: string | null) => void
  setPage: (page: PageContext) => void
  renameThread: (threadId: string, title: string) => Promise<void>
}

const AssistantSessionContext = createContext<AssistantSessionValue | null>(null)

/**
 * Beacon's session for this tab, above the router: the open conversation, any
 * voice call, and whether the widget is open. The widget and the full-page
 * assistant both read it, so moving between pages resets nothing and opening
 * /assistant during a call shows that call rather than starting another.
 *
 * It holds nothing until something shows the assistant, and it is bound to
 * the signed-in person: if another person signs in without the page
 * reloading, the previous person's conversation and call are dropped before
 * anything of theirs could be shown.
 */
export function AssistantSessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(assistantSessionReducer, initialAssistantSession)
  const [threads, setThreads] = useState<AgentThread[]>([])
  const [inquiries, setInquiries] = useState<Inquiry[]>([])
  const [consignments, setConsignments] = useState<Consignment[]>([])
  const [active, setActive] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)

  const refreshThreads = useCallback(() => {
    agentApi
      .listThreads()
      .then(setThreads)
      .catch(() => undefined)
  }, [])

  const onThreadCreated = useCallback(
    (threadId: string) => {
      dispatch({ type: 'thread.selected', threadId })
      refreshThreads()
    },
    [refreshThreads],
  )
  const onThreadTitled = useCallback(
    (threadId: string, title: string) =>
      setThreads((existing) =>
        existing.map((thread) => (thread.id === threadId ? { ...thread, title } : thread)),
      ),
    [],
  )

  const assistant = useAssistant({
    threadId: state.threadId,
    page: state.page,
    onThreadCreated,
    onThreadTitled,
  })

  // Mentionable records are loaded once and held here rather than searched
  // on each keystroke: an operator's open work is a small list, and a local
  // filter responds instantly where a request per character would not.
  useEffect(() => {
    if (!active) return
    let cancelled = false
    agentApi
      .listThreads()
      .then((list) => !cancelled && setThreads(list))
      .catch(() => !cancelled && setThreads([]))
    apiClient
      .listInquiries?.()
      .then((list) => !cancelled && setInquiries(list))
      .catch(() => !cancelled && setInquiries([]))
    apiClient
      .listConsignments?.()
      .then((list) => !cancelled && setConsignments(list))
      .catch(() => !cancelled && setConsignments([]))
    return () => {
      cancelled = true
    }
  }, [active, userId])

  const stopVoice = assistant.voice.stop
  const boundUser = useRef<string | null>(null)
  const bindUser = useCallback(
    (next: string) => {
      const previous = boundUser.current
      boundUser.current = next
      setUserId(next)
      if (previous && previous !== next) {
        stopVoice()
        dispatch({ type: 'thread.selected', threadId: null })
        dispatch({ type: 'panel.closed' })
        setThreads([])
        setInquiries([])
        setConsignments([])
      }
    },
    [stopVoice],
  )

  const renameThread = useCallback(async (threadId: string, title: string) => {
    const updated = await agentApi.renameThread(threadId, title)
    // Patched in place rather than refetching: the list is already correct
    // apart from this one row.
    setThreads((existing) =>
      existing.map((thread) =>
        thread.id === threadId ? { ...thread, title: updated.title } : thread,
      ),
    )
  }, [])

  const mentionItems = useMemo<MentionItem[]>(
    () => [
      ...consignments.map((consignment) => ({
        id: consignment.id,
        label: consignment.code || consignment.title || consignment.id.slice(0, 8),
        sublabel: consignment.customer?.name ?? undefined,
        kind: 'consignment' as const,
      })),
      ...inquiries.map((inquiry) => ({
        id: inquiry.id,
        label: inquiry.customer?.name ?? `Inquiry ${inquiry.id.slice(0, 8)}`,
        sublabel: inquiry.service_type,
        kind: 'inquiry' as const,
      })),
    ],
    [consignments, inquiries],
  )

  // Stable, so a screen can call these from an effect without re-running it.
  const activate = useCallback(() => setActive(true), [])
  const openPanel = useCallback(() => dispatch({ type: 'panel.opened' }), [])
  const closePanel = useCallback(() => dispatch({ type: 'panel.closed' }), [])
  const selectThread = useCallback(
    (threadId: string | null) => dispatch({ type: 'thread.selected', threadId }),
    [],
  )
  const setPage = useCallback((page: PageContext) => dispatch({ type: 'page.changed', page }), [])

  const value = useMemo<AssistantSessionValue>(
    () => ({
      ...state,
      assistant,
      threads,
      mentionItems,
      activate,
      bindUser,
      openPanel,
      closePanel,
      selectThread,
      setPage,
      renameThread,
    }),
    [
      state,
      assistant,
      threads,
      mentionItems,
      activate,
      bindUser,
      openPanel,
      closePanel,
      selectThread,
      setPage,
      renameThread,
    ],
  )

  return (
    <AssistantSessionContext.Provider value={value}>{children}</AssistantSessionContext.Provider>
  )
}

export function useAssistantSession(): AssistantSessionValue {
  const session = useContext(AssistantSessionContext)
  if (!session) throw new Error('useAssistantSession needs an AssistantSessionProvider above it')
  return session
}
